import ReactEcharts from "echarts-for-react";
import { t } from "i18next";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { TbCircleFilled } from "react-icons/tb";
import TimeAxis from "../../../components/Charts/TimeAxis";
import { hexColor } from "../../../enums/colors";
import { calculateDataByEquipment, convertShiftForDate, detectRealtimeShift } from "../../../lib/calculations";
import { useAppStore } from "../../../stores/RootStore";
import { useScreenStore } from "../../../stores/ScreenStore";
import { Equipment, EquipmentAttributes, EquipmentAttributesById, EventsCalculation, FShift } from "../../../types/types";
import { Button } from "../../../ui/Button";
import Spinner from "../../../ui/Spinner/Spinner";
import MonitorHistory from "./MonitorHistory";
import MonitorShow from "./MonitorShow";
import { MonitorStatus } from "./MonitorStatus";
import { MonitorPieLoadChart } from "../../../components/Charts/PieLoadChart/PieLoadChart";

export default function Monitor() {
  const store = useAppStore()
  const {dateShift, shifts, realTimeDateShift, setRealTimeDateShift, setDateShift, isHistory, setIsHistory, updateDateTime, setUpdateDateTime, dataIsLoaded, monitorData, equipments, setStartUpdate} = useScreenStore();
  const [currentDateTime, setCurrentDateTime] = useState(DateTime.local())
  let preEquipments = equipments?.map((e) => {
    if(!e) { return } else {
      return {
        id: String(e.id), 
        type: "equipment_item", 
        attributes: e as unknown as EquipmentAttributes
      }
    }
}) as Equipment[]

  useEffect(() => {
    if(isHistory) { return }
    if(updateDateTime && currentDateTime > updateDateTime) {
      setUpdateDateTime(currentDateTime.plus({minute: 1}).startOf('minute'))
      let rTShift = detectRealtimeShift(shifts!, DateTime.local())
      let currentShift = convertShiftForDate(rTShift, null)
      if(String(currentShift.id) !== String(realTimeDateShift?.id)) {
        setRealTimeDateShift(currentShift)
        setDateShift(currentShift)
      }
      setStartUpdate(true)
    }
    
    const interval = setInterval(() => {
      setCurrentDateTime(DateTime.local());
    }, 1000);

    return () => clearInterval(interval);
  }, [currentDateTime]);

  function resetMonitoring() {
    setIsHistory(false)
    setDateShift(realTimeDateShift!)
  }

  const data = useMemo(() => {
    if (!dateShift || !preEquipments) return { itemsData: [] } as unknown as EventsCalculation;

    return calculateDataByEquipment(monitorData, preEquipments!, dateShift!, store.persons!)
  }, [monitorData, preEquipments, dateShift, store.persons]) 

  return (
    <div className="flex flex-col w-full h-full bg-background overflow-y-scroll">
      <div className="w-full h-full mx-auto max-w-[1288px]">
        {dateShift ? (
          <div className="flex mx-auto h-full py-6 w-full bg-background">
            <div className="flex flex-col text-base gap-y-2 w-full">
              <div className="flex flex-col w-full h-full pb-10">
                <div className="flex flex-col flex-none h-24 justify-center px-6">
                  <div className="flex w-full justify-between items-center mb-2">
                    <div className="flex text-sm">{currentDateTime.toFormat("HH:mm")}</div>
                    <div className="flex gap-2 items-center">
                      {isHistory && <Button variant="link" size={"xs"} onClick={resetMonitoring}>{t('now')}</Button>}
                      <MonitorHistory />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xl font-semibold">{dateShift ? dateShift.start_day.toFormat("dd LLLL yyyy") === dateShift.end_day.toFormat("dd LLLL yyyy") ? dateShift.start_day.toFormat("dd LLLL yyyy") : `${dateShift.start_day.toFormat("dd LLLL yyyy")} - ${dateShift.end_day.toFormat("dd LLLL yyyy")} ` : DateTime.local().toFormat("dd.MM.yyyy")}</span>
                    <div className="flex gap-2">
                      <span>{t('shift')}:</span>
                      <span>{dateShift.label}</span>
                    </div>
                  </div>
                </div>
                {dataIsLoaded ? 
                  <>
                    <div className="px-4">
                      <MonitorPieLoadChart data={data}/>
                    </div>
                    {isHistory ? <></> : <MonitorStatus />}
                    <TimeAxis shiftStart={dateShift?.start_time!} shiftEnd={dateShift?.end_time!} position="top"/>
                    <MonitorShow data={data}/>
                  </>
                : <Spinner />}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex w-full items-center justify-center">{t('screen_not_exist')}</div>
        )}
      </div>
    </div>
  )
}
